import { recordAction } from "@/lib/actionLedger";
import { db } from "@/lib/firebase";
import {
  Policy,
  PolicyState,
  safePolicyParse,
} from "@/schemas/firestore/policy.schema";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

export interface PolicySlice extends PolicyState {
  /* actions */
  subscribeToPolicies: (orgId: string, domainId: string) => () => void;
  setSelectedPolicy: (policyId: string | null) => void;
  addPolicy: (
    orgId: string,
    domainId: string,
    policyData: Omit<
      Policy,
      "id" | "created_at" | "updated_at" | "created_by" | "updated_by" | "domain_id"
    >,
  ) => Promise<string>;
  updatePolicy: (
    orgId: string,
    domainId: string,
    policyId: string,
    policyData: Partial<Policy>,
  ) => Promise<void>;
  deletePolicy: (
    orgId: string,
    domainId: string,
    policyId: string,
  ) => Promise<void>;
  clearPolicies: () => void;
  unsubscribePolicies: () => void;

  /* internals */
  _policySubscriptions: Map<string, () => void>;
}

export const createPolicySlice: StateCreator<
  StoreState,
  [],
  [],
  PolicySlice
> = (set, get) => ({
  /* ---------- initial state ---------- */
  policies: [],
  selectedPolicyId: null,
  isLoading: false,
  error: null,
  _policySubscriptions: new Map(),

  /* ---------- actions ---------- */
  subscribeToPolicies: (orgId: string, domainId: string) => {
    if (!orgId || !domainId) {
      console.log("Policy subscription requires orgId and domainId.");
      return () => {};
    }

    // Check if subscription already exists for this domain
    const existingUnsubscribe = get()._policySubscriptions.get(domainId);
    if (existingUnsubscribe) {
      console.log(
        `Policies subscription already exists for domain ${domainId}`,
      );
      return existingUnsubscribe;
    }

    // Only set loading if no policies exist yet
    set((state) => ({
      isLoading: state.policies.length === 0,
      error: null,
    }));

    console.log(
      `Setting up real-time policies listener for domain ${domainId}`,
    );

    const policiesRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/policies`,
    );
    const q = query(policiesRef, orderBy("created_at", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const policiesList: Policy[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          // TODO: use zod defaults or check if this destructurization if personas, offers and policies is really needed and good
          const policyData = {
            id: docSnapshot.id,
            created_by: data.created_by || "",
            updated_by: data.updated_by ?? null,
            domain_id: data.domain_id || domainId,
            is_enabled: data.is_enabled ?? true,
            name: data.name || "",
            description: data.description || "",
            created_at: data.created_at?.toDate() || new Date(),
            updated_at: data.updated_at?.toDate() || new Date(),
          };

          const result = safePolicyParse(policyData);
          if (result.success) {
            policiesList.push(result.data);
          } else {
            console.error(
              `Validation failed for policy ${docSnapshot.id}:`,
              result.error.issues,
            );
          }
        });

        console.log(
          `Policies snapshot: ${snapshot.docs.length} docs, ${policiesList.length} valid for domain ${domainId}`,
        );

        // Merge with policies of other domains
        set((state) => {
          const other = state.policies.filter((p) => p.domain_id !== domainId);
          return { policies: [...other, ...policiesList], isLoading: false };
        });

        // Sync header tools with updated policies
        get().resetHeaderTools?.();
      },
      (error) => {
        console.error(
          `Policies subscription error for domain ${domainId}:`,
          error,
        );
        set({
          error: "Failed to subscribe to policies",
          isLoading: false,
        });
      },
    );

    // Wrap unsubscribe to remove from Map when called
    const wrappedUnsubscribe = () => {
      unsubscribe();
      get()._policySubscriptions.delete(domainId);
      console.log(`Unsubscribed from policies for domain ${domainId}`);
    };

    // Store wrapped unsubscribe in Map keyed by domainId
    set((state) => {
      const newSubscriptions = new Map(state._policySubscriptions);
      newSubscriptions.set(domainId, wrappedUnsubscribe);
      return { _policySubscriptions: newSubscriptions };
    });

    return wrappedUnsubscribe;
  },

  setSelectedPolicy: (policyId: string | null) => {
    set({ selectedPolicyId: policyId });
  },

  addPolicy: async (
    orgId: string,
    domainId: string,
    policyData: Omit<
      Policy,
      "id" | "created_at" | "updated_at" | "created_by" | "updated_by" | "domain_id"
    >,
  ) => {
    if (!orgId || !domainId) {
      throw new Error(
        "Organization ID and Domain ID are required to add a policy",
      );
    }

    try {
      const policiesRef = collection(
        db,
        `organizations/${orgId}/domains/${domainId}/policies`,
      );
      // `user_id` renamed to `created_by`: it always held exactly this value,
      // and nothing ever queried on it. Pinned to request.auth.uid by rules.
      const newPolicyDoc = await addDoc(policiesRef, {
        ...policyData,
        created_by: get().currentUser?.uid ?? "",
        domain_id: domainId,
        is_enabled: true,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      await recordAction({
        orgId,
        actorUid: get().currentUser?.uid,
        action: "policy.created",
        targetPath: newPolicyDoc.path,
        domainId,
        summary: policyData.name,
      });
      return newPolicyDoc.id;
    } catch (error) {
      console.error("Error adding policy:", error);
      set({ error: "Failed to add policy" });
      throw error;
    }
  },

  updatePolicy: async (
    orgId: string,
    domainId: string,
    policyId: string,
    policyData: Partial<Policy>,
  ) => {
    if (!orgId || !domainId || !policyId) {
      throw new Error(
        "Organization ID, Domain ID, and Policy ID are required to update a policy",
      );
    }

    try {
      const policyRef = doc(
        db,
        `organizations/${orgId}/domains/${domainId}/policies`,
        policyId,
      );
      // `updated_by` is required by firestore.rules on every update, which also
      // refuses any write that would change `created_by`.
      await updateDoc(policyRef, {
        ...policyData,
        updated_at: serverTimestamp(),
        updated_by: get().currentUser?.uid ?? "",
      });
      await recordAction({
        orgId,
        actorUid: get().currentUser?.uid,
        action: "policy.updated",
        targetPath: policyRef.path,
        domainId,
        summary: policyData.name ?? policyId,
      });
    } catch (error) {
      console.error("Error updating policy:", error);
      set({ error: "Failed to update policy" });
      throw error;
    }
  },

  deletePolicy: async (orgId: string, domainId: string, policyId: string) => {
    if (!orgId || !domainId || !policyId) {
      throw new Error(
        "Organization ID, Domain ID, and Policy ID are required to delete a policy",
      );
    }

    try {
      const policyRef = doc(
        db,
        `organizations/${orgId}/domains/${domainId}/policies`,
        policyId,
      );
      // Recorded before the delete: once the document is gone there is no
      // `created_by` left to read, and the deleter would be unattributable.
      await recordAction({
        orgId,
        actorUid: get().currentUser?.uid,
        action: "policy.deleted",
        targetPath: policyRef.path,
        domainId,
        summary: get().policies.find((p) => p.id === policyId)?.name ?? policyId,
      });
      await deleteDoc(policyRef);

      // Clear selected policy if it was the deleted one
      if (get().selectedPolicyId === policyId) {
        set({ selectedPolicyId: null });
      }
    } catch (error) {
      console.error("Error deleting policy:", error);
      set({ error: "Failed to delete policy" });
      throw error;
    }
  },

  clearPolicies: () => {
    set({
      policies: [],
      selectedPolicyId: null,
      isLoading: false,
      error: null,
    });
  },

  unsubscribePolicies: () => {
    const subscriptions = get()._policySubscriptions;
    console.log(
      `Unsubscribing from ${subscriptions.size} policy subscriptions`,
    );

    // Unsubscribe from all domain listeners
    subscriptions.forEach((unsubscribe, domainId) => {
      console.log(`Unsubscribing from policies for domain ${domainId}`);
      unsubscribe();
    });

    // Clear state
    set({
      policies: [],
      selectedPolicyId: null,
      _policySubscriptions: new Map(),
    });
  },
});
